import styled from "styled-components";

import { colors } from "const/colors";
import { colorCycle, platforms } from "const/content";
import { Article, Section, SectionLarge, Text } from "styles";

const Grid = styled.div`
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 16px;
	width: 100%;

	@media (max-width: 900px) {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	@media (max-width: 600px) {
		grid-template-columns: 1fr;
	}
`;

const Card = styled.div`
	background: ${colors.page};
	border-radius: 18px;
	padding: 24px;
`;

const Title = styled.div`
	font-size: 22px;
	font-weight: 800;
	font-family:
		Helvetica Neue,
		Helvetica,
		Arial,
		sans-serif;
	margin-bottom: 8px;
`;

const Pad = styled.div`
	padding: 0 50px 8px;
	width: 100%;
	box-sizing: border-box;

	@media (max-width: 600px) {
		padding: 0 25px 8px;
	}
`;

const Stage = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
	width: 100%;
`;

const MacWrap = styled.div`
	width: min(100%, 920px);
`;

const Pair = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 1fr) minmax(180px, 280px);
	gap: 28px;
	align-items: center;
	width: min(100%, 760px);
	margin-top: 20px;

	@media (max-width: 800px) {
		grid-template-columns: 1fr;
		justify-items: center;
		margin-top: 0;
	}
`;

const IpadWrap = styled.div`
	width: min(100%, 420px);
`;

const Device = styled.div`
	position: relative;
	width: 100%;
	line-height: 0;
`;

const Frame = styled.img`
	position: relative;
	z-index: 1;
	width: 100%;
	height: auto;
	display: block;
	pointer-events: none;
`;

const MacGlass = styled.div`
	position: absolute;
	z-index: 0;
	left: 10.803%;
	top: 11.299%;
	width: 78.368%;
	height: 77.362%;
	background:
		radial-gradient(circle at 20% 15%, rgba(255, 255, 255, 0.45), transparent 36%),
		linear-gradient(
			145deg,
			${colors.purple} 0%,
			${colors.blue} 40%,
			${colors.teal} 72%,
			${colors.magenta} 100%
		);
	overflow: hidden;
`;

const MacShot = styled.img`
	position: absolute;
	left: 4%;
	top: 7%;
	width: 92%;
	height: 88%;
	object-fit: contain;
`;

const IpadShot = styled.img`
	position: absolute;
	z-index: 0;
	left: 5.24%;
	top: 3.818%;
	width: 89.476%;
	height: 92.331%;
	object-fit: cover;
	border-radius: 2%;
`;

const WatchShot = styled.img`
	width: min(100%, 240px);
	height: auto;
	display: block;
	margin: 0 auto;
	border-radius: 28px;
`;

const Caption = styled.div`
	margin-top: 10px;
	text-align: center;
	font-size: 18px;
	font-weight: 800;
	font-family:
		Helvetica Neue,
		Helvetica,
		Arial,
		sans-serif;
	line-height: 1.2;
`;

const Note = styled.div`
	margin: 28px 0 8px;
	background: ${colors.card};
	border-radius: 18px;
	padding: 22px 26px;
	border-left: 6px solid ${colors.teal};
`;

export default function PlatformGrid() {
	return (
		<Section id="platforms">
			<SectionLarge style={{ width: "100%", flexDirection: "column" }}>
				<Article>
					<Text midExtreme>
						<Text color={colors.blue}>Everywhere</Text> you <Text color={colors.teal}>already</Text>{" "}
						work.
					</Text>
					<Text lede style={{ display: "block", marginTop: 20, maxWidth: 720 }}>
						Wishbox is on iPhone, iPad, Mac, and Apple Watch. Optional iCloud or Google Drive keeps
						the same labeled money on every device you turn it on for.
					</Text>
				</Article>
				<Pad>
					<Stage>
						<MacWrap>
							<Device>
								<MacGlass>
									<MacShot src="/assets/screens/mac-window.png" alt="Wishbox on Mac" />
								</MacGlass>
								<Frame src="/assets/frames/macbook.png" alt="" />
							</Device>
							<Caption style={{ color: colors.magenta }}>Mac</Caption>
						</MacWrap>
						<Pair>
							<IpadWrap>
								<Device>
									<IpadShot src="/assets/screens/ipad.png" alt="Wishbox on iPad" />
									<Frame src="/assets/frames/ipad.png" alt="" />
								</Device>
								<Caption style={{ color: colors.teal }}>iPad</Caption>
							</IpadWrap>
							<div>
								<WatchShot src="/assets/screens/apple-watch.png" alt="Wishbox on Apple Watch" />
								<Caption style={{ color: colors.orange }}>Apple Watch</Caption>
							</div>
						</Pair>
					</Stage>
					<Note>
						<Text lede>
							This is the same on <Text color={colors.teal}>Android</Text>. Phones, tablets, and
							Wear OS are still coming, and they ship with these same layouts.
						</Text>
					</Note>
				</Pad>
				<Pad>
					<Grid>
						{platforms.map((platform, index) => (
							<Card key={platform.id}>
								<Title style={{ color: colorCycle[index % colorCycle.length] }}>
									{platform.title}
								</Title>
								<Text body>{platform.body}</Text>
							</Card>
						))}
					</Grid>
				</Pad>
			</SectionLarge>
		</Section>
	);
}
